import { useCallback } from 'react';
import { Button, AlertBoxMsg } from '@qrios/frontend-library';
import { Box, Checkbox, FormControlLabel } from '@mui/material';

import Message from '../message';
import locales from '../../../constants/locales';
import { convertToKebabCase } from '../../../constants/helpers/text';
import { useBeforeUnloadWarning } from '../hooks/useBeforeUnloadWarning';

import InfoField from './infoField';
import DownloadSecrets from './downloadSecrets';
import { SupportMail } from '../support-mail';
import { authentication } from '../../authentication';

interface Props {
  clientId: string;
  clientSecret: string;
  clientIdAndSecretSaved: boolean;
  toggleClientIdAndSecretSaved: () => void;
  handleSubmit: () => void;
  submitText: string;
  isError?: boolean;
  isSubmitLoading?: boolean;
}

export const ClientSecretsForm = ({
  clientId,
  clientSecret,
  clientIdAndSecretSaved,
  submitText,
  isSubmitLoading = false,
  isError = false,
  toggleClientIdAndSecretSaved,
  handleSubmit
}: Props) => {
  useBeforeUnloadWarning(!clientIdAndSecretSaved);

  const handleBackToLogin = useCallback(() => {
    authentication.logout();
  }, []);

  return (
    <Message severity="success">
      {({ setIsPopupOpen, setPopupMessage }) => (
        <Box data-testid="client-secrets-form">
          <InfoField
            popup={{
              setStateCallback: setIsPopupOpen,
              setMsgCallback: setPopupMessage
            }}
            value={clientId}
            label={locales.clientSecrets.label.clientId}
          />
          <InfoField
            popup={{
              setStateCallback: setIsPopupOpen,
              setMsgCallback: setPopupMessage
            }}
            label={locales.clientSecrets.label.clientSecret}
            value={clientSecret}
          />
          <Box marginBottom="10px" display="flex" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  checked={clientIdAndSecretSaved}
                  onChange={toggleClientIdAndSecretSaved}
                  name="dangerConfirm"
                  color="primary"
                  data-testid="checkbox-danger-confirm"
                />
              }
              label={locales.clientSecrets.label.savedChanges}
              data-testid="save-confirmation-checbox-section"
            />
            <DownloadSecrets
              value={`clientId: ${clientId}\nclientSecret: ${clientSecret}`}
              label="secrets"
              popup={{
                setStateCallback: setIsPopupOpen,
                setMsgCallback: setPopupMessage
              }}
            />
          </Box>
          {isError ? (
            <Box mb={3} width="100%">
              <AlertBoxMsg severity="error">
                <Box>
                  <span>
                    Sorry we ran into an unexpected error and your profile could not be updated. Please try again or
                    refresh this page. If the problem still persists, contact{' '}
                  </span>
                  <SupportMail severity="error" />
                  <span>.</span>
                </Box>
              </AlertBoxMsg>
            </Box>
          ) : null}
          <Box mt={3} display="flex" flexDirection="row" width="100%">
            <Box width="50%" pr={1}>
              <Button
                disabled={!clientIdAndSecretSaved || isSubmitLoading}
                loading={isSubmitLoading}
                fullWidth
                text="Back to login page"
                onClick={handleBackToLogin}
                variant="outlined"
                data-testid={`button-${convertToKebabCase('Back to login page')}`}
              />
            </Box>
            <Box width="50%" pr={1}>
              <Button
                disabled={!clientIdAndSecretSaved || isSubmitLoading}
                loading={isSubmitLoading}
                fullWidth
                text={submitText}
                onClick={handleSubmit}
                data-testid={`button-${convertToKebabCase(submitText)}`}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Message>
  );
};
